import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { Keyboard } from '@capacitor/keyboard'
import { Preferences } from '@capacitor/preferences'
import { SplashScreen } from '@capacitor/splash-screen'
import { BundleInfo, CapacitorUpdater } from '@capgo/capacitor-updater'
import { spLogTags } from '@eventr/common'
import { useDrawerStore } from '@eventr/common-storybook'
import { Paths } from '../../routes'
import { bs } from '../../state/bound.store'
import { shownedSnackListSignal } from '../../state/overlayState/overlayContext'
import { captureSentryMessage } from '../../utils'
import { reactAppStage } from '../../utils/reactAppStage'
import { addPushListener } from '../push/pushProvider'

// Track permission request states
let backgroundTimer: NodeJS.Timeout | null = null
const PERMISSION_THRESHOLD = 5000 // 5 seconds threshold to distinguish between permission dialog and actual background

CapacitorUpdater.notifyAppReady()
let data: BundleInfo | null = null
let isSwitchingStage = false

const backButtonMap = {
    [Paths.home]: 'exit',
    '/': 'exit',
    '/authentication/signin': '/',
}

/// Scroll issues on ios, solution to disable setScroll. (think its a auto scroll that messes screen height on scrolling at same time as keyboard changes height)
/// https://github.com/ionic-team/capacitor/issues/1366
if (Capacitor.isNativePlatform()) {
    Keyboard.setScroll({ isDisabled: true })
}

addPushListener()

async function onAppStateChange(isActive: boolean) {
    spLogTags('React App Version', ['UX'], import.meta.env.VITE_APP_VERSION)
    if (isSwitchingStage) return

    if (import.meta.env.VITE_APP_VERSION)
        CapacitorUpdater.setCustomId({
            customId: import.meta.env.VITE_APP_VERSION,
        })

    if (isActive) {
        // App became active
        if (backgroundTimer) {
            clearTimeout(backgroundTimer)
            backgroundTimer = null
        }
        bs().onOpen()
        setTimeout(() => {
            bs().onReturn()
        }, 2000)
        spLogTags('Check for updates if isSwitchingStage === false', ['UPDATE'])
        // Do the download during user active app time to prevent failed download
        const latest = await CapacitorUpdater.getLatest()
        spLogTags('latest version : ', ['UPDATE'], JSON.stringify(latest))
        if (latest.url) {
            try {
                data = await CapacitorUpdater.download({
                    url: latest.url,
                    version: latest.version,
                })

                if (latest.major) {
                    spLogTags(
                        'force downloaded version : ',
                        ['UPDATE'],
                        JSON.stringify(data)
                    )

                    SplashScreen.show()
                    try {
                        await CapacitorUpdater.set(data)
                    } catch (err) {
                        console.log(err)
                        SplashScreen.hide() // in case the set fail, otherwise the new app will have to hide it
                    }
                }
                spLogTags(
                    'downloaded version : ',
                    ['UPDATE'],
                    JSON.stringify(data)
                )
            } catch (e) {
                console.error('download failed', e)
            }
        }
    } else {
        captureSentryMessage(
            'Closing app logs for ' + (bs().profile?.email ?? 'NO_USER')
        )

        shownedSnackListSignal.value = []
        // Create a timer to determine if this is a genuine background event
        // after the permission threshold has passed
        backgroundTimer = setTimeout(() => {
            if (!window.location.pathname.startsWith('/authentication'))
                applyUpdate()
            backgroundTimer = null
        }, PERMISSION_THRESHOLD)
    }
}

async function applyUpdate() {
    if (data) {
        spLogTags('Set the new version', ['UPDATE'])
        // Do the switch when user leave app or when you want
        SplashScreen.show()
        try {
            await CapacitorUpdater.set(data)
        } catch (err) {
            console.log(err)
            SplashScreen.hide() // in case the set fail, otherwise the new app will have to hide it
        }
    }
}
export const registerListeners = async () => {
    await App.removeAllListeners()
    await App.addListener('appStateChange', async ({ isActive }) => {
        bs().syncNotificationPermissions()
        onAppStateChange(isActive)
    })
    await App.addListener('appUrlOpen', async (data) => {
        console.log('App opened with URL:', data)
        const url = new URL(data.url)
        if (url.searchParams.has('stage')) {
            isSwitchingStage = true
            console.log('set path before update : ', url.pathname + url.search)
            console.log(url.searchParams.get('stage') as string)

            switchStage(
                url.searchParams.get('stage') as string,
                url.pathname + url.search
            )
        } else {
            console.log('go to: ', url.pathname + url.search)
            window.location.href =
                window.location.origin + url.pathname + url.search
        }
    })
    await CapacitorUpdater.addListener('appReady', async () => {
        console.log('appReady')

        /// timeouts for a rerender issue on android. Forces a rerender of page when entering app.
        /// reRender elem stored in scroll provider
        setTimeout(() => {
            const tmp: any = document.getElementById('reRender')
            tmp.style.visibility = 'visible'
            tmp.style.opacity = 1

            setTimeout(() => {
                const tmp: any = document.getElementById('reRender')
                tmp.style.visibility = 'hidden'
                tmp.style.opacity = 0
            }, 50)
        }, 200)

        let { value } = await Preferences.get({ key: 'afterUpdatePath' })
        if (value) {
            console.log('afterUpdatePath', value)
            Preferences.remove({ key: 'afterUpdatePath' })
            setTimeout(() => {
                if (value?.startsWith('//sp')) value = value.replace('//sp', '')
                window.location.href = window.location.origin + value
            }, 1000)
        }
    })
    await App.addListener('appRestoredResult', (data) => {
        console.log('Restored state:', data)
    })
    await App.addListener('backButton', ({ canGoBack }) => {
        /// Block back if drawer is open
        const allowBack = !useDrawerStore.getState().openState
        if (!allowBack) {
            return
        }

        if (!canGoBack) {
            App.exitApp()
        } else {
            const path = window.location.pathname
            const backPath = backButtonMap[path]

            if (backPath === 'exit') {
                App.exitApp()
                return
            }
            if (!backPath) {
                window.history.back()
                return
            }
            window.replacePath(backPath)
        }
    })
}

export const switchStage = async (stage: string, path?: string) => {
    const lowerStage = stage.toLowerCase()
    const currentStage = reactAppStage()
    console.log('currentStage', currentStage)

    if (currentStage === lowerStage) {
        if (path) {
            if (path.startsWith('//sp')) path = path.replace('//sp', '')
            window.location.href = window.location.origin + path
        }
        return
    } else {
        Preferences.set({ key: 'afterUpdatePath', value: path ?? '' })
    }
    localStorage.clear()
    localStorage.setItem('REACT_APP_STAGE', lowerStage)
    await switchReactBuild(lowerStage)
}

export const switchReactBuild = async (stage: string) => {
    if (Capacitor.isNativePlatform()) {
        console.log('switchStage', stage)
        SplashScreen.show()
        CapacitorUpdater.setCustomId({
            customId: '0.0.0.0-' + stage,
        })
        console.log('customId setted ', '0.0.0.0-' + stage)

        try {
            const latest = await CapacitorUpdater.getLatest()
            console.log('latest received:' + JSON.stringify(latest))
            if (latest.url) {
                const newUpdate = await CapacitorUpdater.download({
                    url: latest.url,
                    version: latest.version,
                })
                console.log('Download new bundle:', JSON.stringify(newUpdate))
                try {
                    await CapacitorUpdater.set({ id: newUpdate.id })
                } catch (err) {
                    console.log('switchStage done')

                    isSwitchingStage = false
                    console.log(err)
                    await SplashScreen.hide() // in case the set fail, otherwise the new app will have to hide it
                }
            }
        } catch (error) {
            console.log('error on getting update ', error)
            await SplashScreen.hide()
            return
        }
    }
}

onAppStateChange(true)
